import React, { useState, useRef, useEffect } from 'react';
// Fix: Added missing RefreshCw import from lucide-react
import { Camera, X, Sparkles, Loader2, Maximize, Zap, Crosshair, Box, RefreshCw } from 'lucide-react';
import { arDiscoveryService } from '../services/arDiscoveryService';
import { Product } from '../types';

interface Props {
  product: Product;
  onClose: () => void;
}

const ARViewModal: React.FC<Props> = ({ product, onClose }) => {
  const [isScanning, setIsScanning] = useState(true);
  const [placement, setPlacement] = useState<{ x: number, y: number, scale: number, comment: string } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    startAR();
    return () => stopAR();
  }, []);

  const startAR = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (videoRef.current) videoRef.current.srcObject = stream;
      
      // Give it 2 seconds to "warm up" then analyze
      setTimeout(analyzeEnvironment, 2000);
    } catch (e) {
      alert("Camera access required for AR Bhai!");
      onClose();
    }
  };

  const stopAR = () => {
    const stream = videoRef.current?.srcObject as MediaStream;
    stream?.getTracks().forEach(t => t.stop());
  };

  const analyzeEnvironment = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    setIsScanning(true);

    const canvas = canvasRef.current;
    canvas.width = 640;
    canvas.height = 480;
    const ctx = canvas.getContext('2d');
    ctx?.drawImage(videoRef.current, 0, 0, 640, 480);
    const base64 = canvas.toDataURL('image/jpeg', 0.6).split(',')[1];

    const result = await arDiscoveryService.analyzeSpatialFit(base64, product.name);
    if (result && result.confidence > 40) {
      setPlacement(result);
    } else {
      // Retry in 3 seconds if confidence is low
      setTimeout(analyzeEnvironment, 3000);
    }
    setIsScanning(false);
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black flex flex-col items-center justify-center overflow-hidden">
       {/* Camera Feed */}
       <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
       <canvas ref={canvasRef} className="hidden" />

       {/* Overlay HUD */}
       <div className="absolute inset-0 pointer-events-none border-[30px] border-black/20">
          <div className="w-full h-full border border-white/10 rounded-[3rem] relative">
             {/* Corners */}
             <div className="absolute top-8 left-8 w-16 h-16 border-t-4 border-l-4 border-blue-500 rounded-tl-3xl"></div>
             <div className="absolute top-8 right-8 w-16 h-16 border-t-4 border-r-4 border-blue-500 rounded-tr-3xl"></div>
             <div className="absolute bottom-8 left-8 w-16 h-16 border-b-4 border-l-4 border-blue-500 rounded-bl-3xl"></div>
             <div className="absolute bottom-8 right-8 w-16 h-16 border-b-4 border-r-4 border-blue-500 rounded-br-3xl"></div>
             
             {/* Scanning Animation */}
             {isScanning && (
               <div className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent animate-bounce opacity-50 top-1/2"></div>
             )}
          </div>
       </div>

       {/* AI Placement Hud */}
       {placement && (
         <div 
           className="absolute transition-all duration-1000 ease-out"
           style={{ 
             left: `${placement.x}%`, 
             top: `${placement.y}%`, 
             transform: `translate(-50%, -50%) scale(${placement.scale})` 
           }}
         >
            <div className="relative group/ar">
               <div className="absolute -inset-10 bg-blue-500/10 blur-3xl animate-pulse"></div>
               <div className="w-48 h-48 bg-white/20 backdrop-blur-md rounded-[3rem] border-2 border-white/40 shadow-2xl flex items-center justify-center p-4">
                  <img src={product.images[0]} className="w-full h-full object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)]" />
               </div>
               {/* 3D Indicators */}
               <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-[8px] font-black uppercase tracking-widest whitespace-nowrap flex items-center gap-2">
                  <Box className="h-3 w-3" /> Spatial Anchored
               </div>
            </div>
         </div>
       )}

       {/* Top Bar */}
       <div className="absolute top-10 left-10 right-10 flex justify-between items-start pointer-events-none">
          <div className="bg-black/60 backdrop-blur-xl border border-white/20 p-6 rounded-[2.5rem] flex items-center gap-6 pointer-events-auto shadow-2xl">
             <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-white">
                <Sparkles className="h-6 w-6" />
             </div>
             <div>
                <h3 className="text-white font-black uppercase tracking-tight text-sm">Spatial Discovery v2</h3>
                <p className="text-blue-400 text-[10px] font-bold uppercase tracking-widest">
                   {isScanning ? 'Mapping Environment...' : placement ? 'Product Virtualized' : 'Searching for surface...'}
                </p>
             </div>
          </div>
          <button 
            onClick={onClose}
            className="p-5 bg-white/10 backdrop-blur-xl rounded-full text-white pointer-events-auto hover:bg-white/20 transition-all border border-white/20"
          >
             <X className="h-8 w-8" />
          </button>
       </div>

       {/* Bottom Intel */}
       <div className="absolute bottom-12 left-10 right-10 flex flex-col items-center gap-6">
          {placement?.comment && (
            <div className="bg-white p-6 rounded-[2rem] border border-blue-100 shadow-2xl max-w-sm text-center animate-in slide-in-from-bottom-5">
               <p className="text-gray-800 text-sm font-bold italic leading-relaxed">
                  <Sparkles className="h-4 w-4 inline mr-2 text-blue-600" />
                  "{placement.comment}"
               </p>
            </div>
          )}
          <div className="flex gap-4">
             <button onClick={analyzeEnvironment} className="bg-white text-gray-900 px-10 py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-2xl flex items-center gap-3">
                <RefreshCw className={`h-4 w-4 ${isScanning ? 'animate-spin' : ''}`} /> Remap Spatial Info
             </button>
          </div>
       </div>
    </div>
  );
};

export default ARViewModal;